/**
 * Display labels for filter-catalog fields in the policy tree builder.
 * Prefer report/pick vocabulary (e.g. "Regime") over long catalog titles
 * like "Market condition (candle-only…)".
 */

import { marketCandleDirectionLabel } from "./marketRegimeDirectionLookbacks";

/** Strip trailing parenthetical qualifiers from catalog labels. */
export function stripCatalogLabelQualifiers(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return label;
  const without = trimmed.replace(/(\s*\([^)]+\))+\s*$/, "").trim();
  return without || trimmed;
}

/**
 * Path → pick/report display name. Keep in sync with
 * `getSliceCategoryLabel` / condition-analysis pick titles where possible.
 */
const POLICY_FIELD_PICK_LABELS: Record<string, string> = {
  "regime.market_condition": "Regime",
  "structure.candle_regime": "Regime",
  "structure.market_regime": "Regime",
  "context.candle_regime": "Regime",
  "entry.candle_regime": "Regime",
  "entry.context.candle_regime": "Regime",
  "trade.trade_type": "Trade direction",
  "regime.direction": marketCandleDirectionLabel("long"),
  "structure.candle_direction": marketCandleDirectionLabel("long"),
  "context.candle_direction": marketCandleDirectionLabel("long"),
  "entry.candle_direction": marketCandleDirectionLabel("long"),
  "regime.direction_short": marketCandleDirectionLabel("short"),
  "structure.candle_direction_short": marketCandleDirectionLabel("short"),
  "structure.direction_short": marketCandleDirectionLabel("short"),
  "context.direction_short": marketCandleDirectionLabel("short"),
  "entry.candle_direction_short": marketCandleDirectionLabel("short"),
  "entry.direction_short": marketCandleDirectionLabel("short"),
  "structure.adx_bucket": "ADX",
  "volatility.atr_regime": "ATR regime",
  "volatility.bollinger_zone": "Bollinger zone",
  "volatility.bollinger.position": "Bollinger position",
  "context_consistency.consistency_bucket": "Consistency",
  "htf_alignment.weighted_alignment_score": "HTF alignment",
  "sr_positioning.strength": "SR strength",
  "sr_positioning.room": "SR room",
  "sr_positioning.sr_room": "SR room",
  "trend_location.price_vs_ma.sma_50": "Price vs MA50",
  "trend_location.price_vs_ma.sma_200": "Price vs MA200",
};

export function policyCatalogFieldDisplayLabel(
  path: string,
  catalogLabel?: string | null
): string {
  const key = String(path ?? "").trim();
  if (key && POLICY_FIELD_PICK_LABELS[key]) return POLICY_FIELD_PICK_LABELS[key]!;
  if (catalogLabel != null && String(catalogLabel).trim()) {
    return stripCatalogLabelQualifiers(String(catalogLabel));
  }
  return key || "Field";
}

/**
 * Humanize situation/bucket pick titles for the simulator cart so they match
 * field picker vocabulary (e.g. trend_state=weak_or_ranging → ADX: weak).
 */
export function humanizeSituationPickLabel(label: string): string {
  const raw = String(label ?? "").trim();
  if (!raw) return raw;
  if (!raw.includes("=") && !raw.includes(":")) return raw;

  const parts = raw.split("|").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return raw;

  const keyLabel: Record<string, string> = {
    trend_state: "ADX",
    adx: "ADX",
    candle_regime: "Regime",
    dir: "Market candle direction",
    dir_short: "Market candle direction (short)",
    atr_regime: "ATR regime",
    bb_zone: "Bollinger zone",
    bollinger: "Bollinger position",
    consistency: "Consistency",
    htf_weighted: "HTF alignment",
    sr_room: "SR room",
    sr_strength: "SR strength",
    ma50: "Price vs MA50",
    ma200: "Price vs MA200",
    session: "Trading session",
    squeeze: "Squeeze",
    rsi: "RSI",
  };

  const valueRewrite: Record<string, Record<string, string>> = {
    trend_state: { weak_or_ranging: "weak", ranging: "weak" },
  };

  const out: string[] = [];
  for (const part of parts) {
    const ieq = part.indexOf("=");
    const icol = part.indexOf(":");
    let splitAt = -1;
    if (ieq > 0) splitAt = ieq;
    else if (icol > 0) splitAt = icol;
    else {
      out.push(part);
      continue;
    }
    const key = part.slice(0, splitAt).trim().toLowerCase().replace(/\s+/g, "_");
    let value = part.slice(splitAt + 1).trim();
    const rewritten = valueRewrite[key]?.[value.toLowerCase()];
    if (rewritten) value = rewritten;
    const prettyKey = keyLabel[key] ?? key.replace(/_/g, " ");
    out.push(`${prettyKey}: ${value}`);
  }
  return out.join(" · ");
}
